import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, NumberInput, Button } from '@mantine/core';
import { IconUsersGroup, IconCheck, IconX, IconUsers, IconBabyCarriage, IconBabyCarriageFilled, IconEdit } from '@tabler/icons-react';
import { ShowPaxGroup } from '../snippets';

interface Props {
    adults: number;
    children: number;
    seniors: number;
    infants: number;
    guests?: number;
    onAccept?: (counts: { adults: number; children: number; seniors: number; infants: number; guests: number }) => void;
    onCancel?: () => void;
    names?: string[];
    target?: React.ReactNode;
    isLoading?: boolean;
    disabled?: boolean;
}

export default function GroupSizePopover({
    adults,
    children,
    seniors,
    infants,
    guests = 0,
    onAccept = () => { },
    onCancel = () => { },
    names = [],
    target,
    isLoading = false,
    disabled = false,
}: Props) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);

    // Internal draft state for the form inputs
    const [draftAdults, setDraftAdults] = useState(adults);
    const [draftChildren, setDraftChildren] = useState(children);
    const [draftSeniors, setDraftSeniors] = useState(seniors);
    const [draftInfants, setDraftInfants] = useState(infants);
    const [draftGuests, setDraftGuests] = useState(guests);

    // Keep draft state in sync with external props whenever the popover is closed or props change
    useEffect(() => {
        if (!opened) {
            setDraftAdults(adults);
            setDraftChildren(children);
            setDraftSeniors(seniors);
            setDraftInfants(infants);
            setDraftGuests(guests);
        }
    }, [adults, children, seniors, infants, guests, opened]);

    const handleAccept = () => {
        onAccept({
            adults: draftAdults,
            children: draftChildren,
            seniors: draftSeniors,
            infants: draftInfants,
            guests: draftGuests
        });
        setOpened(false);
    };

    const handleCancel = () => {
        onCancel?.();
        setDraftAdults(adults);
        setDraftChildren(children);
        setDraftSeniors(seniors);
        setDraftInfants(infants);
        setDraftGuests(guests);
        setOpened(false);
    };

    return (
        <Popover
            withinPortal
            zIndex={2000}
            opened={opened}
            onChange={(val) => setOpened(val)}
            position="bottom-start"
            withArrow
            shadow="md"
            radius="md"
            disabled={disabled}
            closeOnClickOutside
            closeOnEscape
            trapFocus
        >
            <Popover.Target>
                {target ? (
                    <div
                        onClick={() => !disabled && setOpened(!opened)}
                        className="cursor-pointer">
                        {target}
                    </div>
                ) : (
                    <div
                        onClick={() => !disabled && setOpened(!opened)}
                        className={disabled ? 'bg-gray-100 px-1 py-0.5 rounded-md' : "solid-box-border-hover"}
                    >
                        <div className='flex gap-1 items-center justify-end'>
                            <div className='max-w-40 truncate text-right font-semibold text-xs'>
                                {names.join(', ')}
                            </div>
                            <ShowPaxGroup
                                adults={adults}
                                children={children}
                                seniors={seniors}
                                infants={infants}
                                guests={guests}
                            />
                        </div>
                    </div>
                )}
            </Popover.Target>
            <Popover.Dropdown p="md" className="w-64">
                <div className="flex flex-col gap-3">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                        {t('group_composition', { defaultValue: 'Group Composition' })}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <NumberInput
                                label={t('adults')}
                                size="xs"
                                min={0}
                                hideControls
                                value={draftAdults || ''}
                                onChange={(val) => setDraftAdults(Number(val))}
                            />
                        </div>
                        <div>
                            <NumberInput
                                label={t('children')}
                                size="xs"
                                min={0}
                                hideControls
                                value={draftChildren || ''}
                                onChange={(val) => setDraftChildren(Number(val))}
                            />
                        </div>
                        <div>
                            <NumberInput
                                label={t('seniors')}
                                size="xs"
                                min={0}
                                hideControls
                                value={draftSeniors || ''}
                                onChange={(val) => setDraftSeniors(Number(val))}
                            />
                        </div>
                        <div>
                            <NumberInput
                                label={t('infants')}
                                size="xs"
                                min={0}
                                hideControls
                                value={draftInfants || ''}
                                onChange={(val) => setDraftInfants(Number(val))}
                            />
                        </div>
                        <div className="col-span-2 max-w-80">
                            <NumberInput
                                label={t('guests_label')}
                                description={t('guests_description')}
                                size="xs"
                                min={0}
                                hideControls
                                value={draftGuests || ''}
                                onChange={(val) => setDraftGuests(Number(val))}
                            />
                        </div>
                    </div>

                    <div className="flex justify-between items-center mt-2 gap-2">
                        <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            radius="md"
                            onClick={handleCancel}
                            leftSection={<IconX size={14} />}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            size="xs"
                            color="black"
                            radius="md"
                            onClick={handleAccept}
                            loading={isLoading}
                            leftSection={<IconCheck size={14} />}
                        >
                            {t('accept')}
                        </Button>
                    </div>
                </div>
            </Popover.Dropdown>
        </Popover>
    );
}
